import Message from '@/fsd/shared/ui/AutocompleteSelect/components/Message/Message.jsx';
import cx from 'classnames';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from '../AutocompleteSelect/hooks/useClickOutside';
import { usePortalPlacement } from '../AutocompleteSelect/hooks/usePortalPlacement';
import DownArrow from '../icons/DownArrow';
import InfoIcon from '../icons/info';
import { Input } from '../input/input';
import { Tooltip } from '../tooltip/tooltip';
import TruncatedTextWithTooltip from '../TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import { getFilterOptions } from './helpers/getFilterOptions';
import style from './style.module.scss';
import { observer } from 'mobx-react-lite';

export interface Option {
  id: string | number;
  name: string;
  value: string;
  isDisabled?: boolean;
}

interface CustomSelectProps {
  options: Option[];
  value?: string | number;
  onOptionChange: (option: Option) => void;
  fieldName?: string;
  disabled?: boolean;
  className?: string;
  classNameOptionList?: string;
  title?: string;
  width?: string;
  placeholder?: string;
  openDirection?: 'top' | 'bottom' | 'right' | 'left';
  isUseInput?: boolean;
  usePortal?: boolean;
  tooltipTextForInfo?: ReactNode;
}

const CustomSelect: React.FC<CustomSelectProps> = observer(
  ({
    options,
    value,
    onOptionChange,
    disabled = false,
    className = '',
    classNameOptionList = '',
    title = '',
    width = '',
    placeholder = 'Выберите из списка',
    openDirection = 'bottom',
    isUseInput = false,
    usePortal = true,
    tooltipTextForInfo,
    ...props
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const selectRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options?.find(
      (opt) =>
        (opt.value && opt.value === value) || (opt.id && opt.id === value)
    );

    useClickOutside([selectRef, listRef], () => {
      setIsOpen(false);
      setSearch('');
    });

    const listCoords = usePortalPlacement(selectRef, isOpen);

    useEffect(() => {
      if (isOpen && isUseInput) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }, [isOpen, isUseInput]);

    const handleOptionClick = (option: Option) => {
      onOptionChange(option);
      setIsOpen(false);
      setSearch('');
    };

    const toggleDropdown = () => {
      if (!disabled) {
        if (isOpen) {
          setSearch('');
        }
        setIsOpen(!isOpen);
      }
    };

    const filteredOptions = getFilterOptions({ isUseInput, options, search });

    const renderOptionList = () => (
      <div
        ref={listRef}
        style={
          usePortal
            ? {
                position: 'absolute',
                top: `${listCoords?.top}px`,
                left: `${listCoords?.left}px`,
                width: `${listCoords?.width}px`,
                zIndex: 1051,
              }
            : {}
        }
      >
        <div
          className={cx(
            style.optionList,
            isOpen ? style.active : '',
            classNameOptionList
          )}
          data-testid="custom-select-optionList"
        >
          {!Array.isArray(filteredOptions) || filteredOptions.length === 0 ? (
            <Message
              text={
                isUseInput && search
                  ? 'Нет совпадений'
                  : 'Нет доступных элементов'
              }
              className={style.option}
            />
          ) : (
            filteredOptions.map((option) => (
              <div
                className={cx(
                  style.option,
                  selectedOption?.id === option.id ? style.selected : ''
                )}
                key={option.id}
                onClick={() => handleOptionClick(option)}
                data-testid={`custom-select-option-${option.name}`}
              >
                {option.name}
              </div>
            ))
          )}
        </div>
      </div>
    );

    return (
      <div
        ref={selectRef}
        className={cx(
          style.customSelect,
          style[`openDirection--${openDirection}`],
          className
        )}
        style={{ width: width || '' }}
        data-testid="custom-select"
        aria-disabled={disabled}
        {...props}
      >
        {title && <div className={style.title}>{title}</div>}

        <div
          className={cx(
            style.customSelectContent,
            isOpen ? style.active : '',
            disabled ? style.disabled : ''
          )}
        >
          <div
            className={cx(
              style.button,
              isOpen ? style.active : '',
              disabled ? style.disabled : '',
              !selectedOption ? style.placeholder : style.selectedValue
            )}
            onClick={toggleDropdown}
            data-testid="custom-select-button"
          >
            {isUseInput && isOpen ? (
              <Input
                ref={inputRef}
                value={search}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={selectedOption?.name || placeholder || 'Поиск...'}
                variant="without-borders"
              />
            ) : (
              <TruncatedTextWithTooltip
                text={selectedOption?.name || placeholder}
                maxLines={1}
              />
            )}
            <div className={style.icons}>
              {tooltipTextForInfo && (
                <Tooltip content={tooltipTextForInfo} placement="right">
                  <span onClick={(e) => e.stopPropagation()}>
                    <InfoIcon className={style.infoIcon} />
                  </span>
                </Tooltip>
              )}
              <DownArrow className={style.arrow} />
            </div>
          </div>
          {isOpen &&
            !disabled &&
            (usePortal ? listCoords : true) &&
            (usePortal
              ? createPortal(renderOptionList(), document.body)
              : renderOptionList())}
        </div>
      </div>
    );
  }
);

export default CustomSelect;
